/**
 * Session menu — restart, save, load, settings, exit.
 *
 * Two panels rather than one, so they spread across the screen instead of
 * stacking into a column of buttons.
 *
 * DESTRUCTIVE ACTIONS ARM FIRST. Restart and Load both throw away the running
 * colony, and on a touch screen a mis-tap is a lost session. The first press
 * turns the button into a confirmation that says what will be lost; the second
 * does it. Arming clears itself after a few seconds so a button never sits
 * quietly primed for later.
 */

import { el, section, setText, type Panel } from './panel';
import type { GameState } from '../../sim/types';

export interface MenuActions {
  restart(): void;
  save(): void;
  load(): void;
  togglePause(): void;
  paused(): boolean;
  cycleTheme(): string;
  themeName(): string;
  /** Describes what is in the save slot, or null when the slot is empty. */
  savedSummary(): string | null;
  /** Puts a diagnostic on the clipboard. Resolves to what to tell the player. */
  copyReport(): Promise<string>;
  exit(): void;
}

/** How long an armed confirmation stays armed. */
const ARM_MS = 4000;

/**
 * A button that asks once before doing something irreversible.
 *
 * The timer is stored so a second arming cannot leave the first one running and
 * disarm the button out from under the player a moment after they armed it.
 */
function arming(
  label: string,
  confirmLabel: string,
  onConfirm: () => void,
): HTMLButtonElement {
  const button = el('button', 'action', label);
  let armed = false;
  let timer = 0;

  const disarm = (): void => {
    armed = false;
    window.clearTimeout(timer);
    setText(button, label);
    button.classList.remove('toggle-on');
  };

  button.addEventListener('click', () => {
    if (armed) {
      disarm();
      onConfirm();
      return;
    }
    armed = true;
    setText(button, confirmLabel);
    button.classList.add('toggle-on');
    window.clearTimeout(timer);
    timer = window.setTimeout(disarm, ARM_MS);
  });

  return button;
}

export function createSessionPanel(actions: MenuActions): Panel {
  const { root, body } = section('Session');

  const status = el('div', 'hint');
  const saveState = el('div', 'hint');

  const row = el('div', 'button-row');
  const saveButton = el('button', 'action', 'Save');
  saveButton.addEventListener('click', actions.save);

  const loadButton = arming('Load', 'Discard and load?', actions.load);
  const restartButton = arming('Restart', 'Discard and restart?', actions.restart);

  // For playtesters: everything needed to reproduce or diagnose a session,
  // small enough to paste into a message. The full save stays in the save slot;
  // what a report needs is which build, which world, and where it had got to.
  const reportButton = el('button', 'action', 'Copy report');
  reportButton.addEventListener('click', () => {
    void actions.copyReport().then((message) => {
      setText(reportButton, message);
      window.setTimeout(() => setText(reportButton, 'Copy report'), ARM_MS);
    });
  });

  row.append(saveButton, loadButton, restartButton, reportButton);
  body.append(status, saveState, row);

  return {
    root,
    update(state: GameState): void {
      setText(
        status,
        `Tick ${state.tick} · seed ${state.seed} · Alpha ${__APP_VERSION__} · build ${__BUILD_ID__}`,
      );
      const summary = actions.savedSummary();
      setText(saveState, summary ?? 'No saved session.');
      loadButton.disabled = summary === null;
    },
  };
}

export function createSettingsPanel(actions: MenuActions): Panel {
  const { root, body } = section('Settings');

  const row = el('div', 'button-row');

  const pause = el('button', 'action', 'Pause');
  pause.addEventListener('click', () => {
    actions.togglePause();
  });

  const theme = el('button', 'action', 'Theme');
  theme.addEventListener('click', () => {
    setText(theme, `Theme: ${actions.cycleTheme()}`);
  });
  setText(theme, `Theme: ${actions.themeName()}`);

  row.append(pause, theme);

  // A web page cannot close itself unless a script opened it, so this says what
  // it actually does rather than promising a quit that would not happen.
  const exitRow = el('div', 'button-row');
  const exit = arming('Exit', 'Stop the session?', actions.exit);
  exitRow.append(exit);

  body.append(
    row,
    el(
      'div',
      'hint',
      'Pausing stops the simulation; the console stays readable. Exit halts the session — a page cannot close itself, so the tab remains open.',
    ),
    exitRow,
  );

  return {
    root,
    update(): void {
      setText(pause, actions.paused() ? 'Resume' : 'Pause');
      pause.classList.toggle('toggle-on', actions.paused());
    },
  };
}
